import { useCallback, useMemo } from 'react';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import {
  ApprovalStepState,
  ProposalAgent,
  ProposalProps,
} from '~/data/proposals/types';
import { isRoleHRAdmin } from '~/services/Permissions';
import { getFirstInArray } from '~/utils/helper';

export enum ProcessAgencyRole {
  Initiator = 'initiator',
  Approver = 'approver',
  Overseer = 'overseer',
  Administrator = 'administrator',
  Ultimate = 'ultimate',
}

type PropsType = {
  proposal?: ProposalProps;
  substituteAsUltimateApprover?: boolean;
};

export const useLoggedInProposalAgency = (props: PropsType) => {
  const { proposal } = props;

  // Logged user data
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const loggedInUserRoles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(loggedInUserRoles);

  // Resolve user agency on proposal process step
  const findApprovalStep = useCallback(
    (stepSeq: number, steps?: ApprovalStepState[]) => {
      return steps?.find((step) => step.seq === stepSeq);
    },
    [],
  );

  const isAgentUser = useCallback(
    (agent: ProposalAgent): boolean => {
      return agent.userId === loggedInEmployeeId;
    },
    [loggedInEmployeeId],
  );

  const findUserInAgents = useCallback(
    (agents?: ProposalAgent[]): ProposalAgent | undefined => {
      return agents?.find((a) => isAgentUser(a));
    },
    [isAgentUser],
  );

  const filterUndefinedFromAgents = useCallback(
    (agencyRoles: (ProposalAgent | undefined)[]): ProposalAgent[] => {
      return agencyRoles.filter((role): role is ProposalAgent => !!role);
    },
    [],
  );

  // HR Admins are not specified in proposal agents, but have agency
  const extendAgentsWithHRAdmin = useCallback(
    (agents?: ProposalAgent[]): ProposalAgent[] => {
      return isHRAdmin && !!agents
        ? [
            ...agents,
            {
              userId: loggedInEmployeeId,
              roleName: 'HR_ADMIN',
            },
          ]
        : agents || [];
    },
    [isHRAdmin, loggedInEmployeeId],
  );

  const usersRoleInProcess = useMemo(() => {
    const approverOnSteps =
      proposal?.approvalSteps
        ?.map((step) => ({
          ...step,
          approvers: findUserInAgents(step.approvers),
        }))
        .filter((step) => !!step.approvers) || [];

    // TODO Resolve initiators from proposal preview data, when endpoint exists
    const initiators: ProposalAgent[] = [];

    const allAdmins = extendAgentsWithHRAdmin(proposal?.administrators);

    const resolved = {
      initiator: findUserInAgents([...initiators, ...allAdmins]),
      approverOnSteps,
      overseer: findUserInAgents(proposal?.overseers),
      administrator: findUserInAgents(allAdmins),
    };

    return {
      ...resolved,
      ultimate: filterUndefinedFromAgents([
        resolved.overseer,
        resolved.administrator,
      ]),
    };
  }, [
    proposal,
    findUserInAgents,
    extendAgentsWithHRAdmin,
    filterUndefinedFromAgents,
  ]);

  const getAgency = useCallback(
    (agencyRoles: (ProposalAgent | undefined)[]) => {
      return getFirstInArray(filterUndefinedFromAgents(agencyRoles));
    },
    [filterUndefinedFromAgents],
  );

  const getApprovalAgencyOnStep = useCallback(
    (stepSeq: number): ProposalAgent | undefined => {
      const step = findApprovalStep(stepSeq, proposal?.approvalSteps);
      const stepApprover = findUserInAgents(step?.approvers || []);

      return getAgency([
        stepApprover,
        usersRoleInProcess.overseer,
        usersRoleInProcess.administrator,
      ]);
    },
    [
      proposal,
      usersRoleInProcess,
      findUserInAgents,
      findApprovalStep,
      getAgency,
    ],
  );

  const getAgencyInRole = useCallback(
    (role: ProcessAgencyRole): ProposalAgent | undefined => {
      switch (role) {
        case ProcessAgencyRole.Initiator:
          return usersRoleInProcess.initiator;
        case ProcessAgencyRole.Approver:
          return usersRoleInProcess?.approverOnSteps
            ?.map((step) => step.approvers)
            .flat()[0];
        case ProcessAgencyRole.Overseer:
          return usersRoleInProcess.overseer;
        case ProcessAgencyRole.Administrator:
          return usersRoleInProcess.administrator;
        case ProcessAgencyRole.Ultimate:
          return getFirstInArray(usersRoleInProcess.ultimate);
        default:
          return undefined;
      }
    },
    [usersRoleInProcess],
  );

  return {
    usersRoleInProcess,
    getApprovalAgencyOnStep,
    getAgencyInRole,
  };
};
